import { useTranslation } from 'react-i18next';
import { ShoppingBag } from 'lucide-react';
import { Icon } from '@/components/ui/icon';
import { Tabs } from '@/components/ui/tabs';
import { useRoutedTabs } from '@/Pages/routedTabs';
import { DELIVERY_AGGREGATORS_TABS } from '@/Pages/tabRoutes';
import IncomingOrdersTab from './IncomingOrdersTab';
import MappingsTab from './MappingsTab';
import UnreconciledTab from './UnreconciledTab';

/**
 * ADR-0028 Part B — the surface that was missing entirely: ten backend routes
 * (§ read directly off `delivery-aggregator-orders.controller.ts` and
 * `delivery-aggregator-mappings.controller.ts`) with no dashboard reach before
 * this screen. Three tabs, one per job an operator actually has:
 *
 *  - **Incoming** is the gate (§B2/§B6) — see and accept/reject an order
 *    before it ever reaches the kitchen, regardless of the venue's own
 *    waiter-confirmation setting.
 *  - **Mappings** is why an order can be un-fulfillable at all (§B4) — map a
 *    marketplace item to the catalogue, and toggle the marketplace's own
 *    sold-out flag independently of `branch_menu_rules`.
 *  - **Unreconciled** is the one honest gap this design accepts and names in
 *    its own operator doc: an ingestion failure after the webhook's own
 *    dedupe has already claimed it is invisible everywhere else.
 */
export default function DeliveryAggregators() {
  const { t } = useTranslation();
  const routed = useRoutedTabs('/delivery-aggregators', DELIVERY_AGGREGATORS_TABS);

  if (!routed.open) return routed.redirect;
  const { tabs, current, select } = routed;

  return (
    <div className="space-y-5">
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-semibold">
          <Icon as={ShoppingBag} size="lg" />
          {t('deliveryAggregators.title')}
        </h1>
        <p className="text-sm text-ink-muted">{t('deliveryAggregators.subtitle')}</p>
      </div>

      <Tabs
        tabs={tabs.map((tab) => ({ key: tab.id, label: t(tab.labelKey) }))}
        value={current}
        onChange={select}
        label={t('deliveryAggregators.title')}
        idPrefix="delivery-aggregators"
        className="scrollbar-thin gap-x-5 overflow-x-auto"
      />

      {current === 'incoming' && <IncomingOrdersTab />}
      {current === 'mappings' && <MappingsTab />}
      {current === 'unreconciled' && <UnreconciledTab />}
    </div>
  );
}
